/**
 * The metal edge on a shelf card: a bevelled bar across the top and a hairline
 * frame of the same metal that fades down the sides.
 *
 * Owner's brief (2026-10-14): every card gets its own top edge, a 3D gradient
 * that runs top to bottom, "fashionable, not colorful", silver or gold or the
 * like, chosen per card so it stands out against the game's own colour.
 *
 * So the palette is metals only, and the metal is picked AGAINST the game's
 * tint: a warm game (orange, red, amber…) gets a cool metal (silver, platinum,
 * titanium), a cool game (blue, violet, teal…) a warm one (gold, champagne,
 * bronze, rose gold). Within that family a hash of the id picks the metal, so
 * the choice looks random across the shelf but a game keeps its metal on every
 * visit and every device.
 */

import type { CSSProperties } from 'react';
import type { Game } from '@/lib/games';

interface Metal {
  name: string;
  /** Bevel, top to bottom: the lit edge, the face, the turn, the shadowed lip. */
  hi: string; face: string; turn: string; lip: string;
  /** The metal as r,g,b for the fading side frame and the glow. */
  rgb: string;
}

const METALS: Record<string, Metal> = {
  gold:      { name: 'Gold',      hi: '#fff6d2', face: '#e9c46a', turn: '#b8862b', lip: '#6e4c12', rgb: '214,170,80' },
  champagne: { name: 'Champagne', hi: '#fffaf0', face: '#e8d5b0', turn: '#bfa77c', lip: '#7a6648', rgb: '222,200,160' },
  bronze:    { name: 'Bronze',    hi: '#ffe1c2', face: '#c98d55', turn: '#94602f', lip: '#552f12', rgb: '196,134,80' },
  rosegold:  { name: 'Rose gold', hi: '#ffe8e1', face: '#e3a99a', turn: '#b4776a', lip: '#6b3d35', rgb: '220,160,145' },
  silver:    { name: 'Silver',    hi: '#ffffff', face: '#d4d8de', turn: '#9aa1ab', lip: '#545b66', rgb: '200,206,214' },
  platinum:  { name: 'Platinum',  hi: '#ffffff', face: '#e6ebf2', turn: '#b3bcc9', lip: '#68717f', rgb: '226,232,240' },
  titanium:  { name: 'Titanium',  hi: '#e9edf2', face: '#9ba3ae', turn: '#6b737e', lip: '#33383f', rgb: '150,158,170' },
};

const COOL_METALS = ['silver', 'platinum', 'titanium'];
const WARM_METALS = ['gold', 'champagne', 'bronze', 'rosegold'];

/** Warm tints take a cool metal; cool tints a warm one; greens look best in the golds. */
const WARM_TINTS = new Set(['orange', 'amber', 'yellow', 'red', 'rose', 'pink']);
const GREEN_TINTS = new Set(['lime', 'emerald', 'green']);

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

export function metalFor(game: Pick<Game, 'id' | 'tint'>): Metal {
  const t = game.tint;
  const family = !t ? [...WARM_METALS, ...COOL_METALS]
    : WARM_TINTS.has(t) ? COOL_METALS
    : GREEN_TINTS.has(t) ? ['gold', 'champagne', 'rosegold']
    : WARM_METALS;
  return METALS[family[hash(game.id) % family.length]];
}

/**
 * Drop inside a `relative overflow-hidden rounded-md group` card with `pt-[20px]`,
 * so the 20px bar is a real top border above the content, not drawn over it.
 * Never takes a click.
 */
export default function MetalFrame({ game }: { game: Pick<Game, 'id' | 'tint'> }) {
  const m = metalFor(game);
  const frame: CSSProperties = {
    padding: 1.5,
    background: `linear-gradient(180deg, ${m.hi} 0%, ${m.face} 6%, rgba(${m.rgb},.85) 22%, rgba(${m.rgb},.45) 55%, rgba(${m.rgb},.22) 100%)`,
    WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    WebkitMaskComposite: 'xor',
    mask: 'linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)',
  };
  const bar: CSSProperties = {
    /* A rounded bar lit from above: bright rim, face, a specular band where the
       curve catches the light, then the turn into shadow and the dark lip. */
    background: `linear-gradient(180deg, ${m.hi} 0%, ${m.face} 16%, ${m.hi} 34%, ${m.face} 50%, ${m.turn} 78%, ${m.lip} 100%)`,
    boxShadow: `inset 0 1px 0 rgba(255,255,255,.7), inset 0 -1px 0 rgba(0,0,0,.35), 0 1px 0 rgba(0,0,0,.55), 0 3px 12px rgba(${m.rgb},.40)`,
  };
  return (
    <>
      <span aria-hidden className="pointer-events-none absolute inset-0 z-10 rounded-md opacity-90 transition-opacity duration-300 group-hover:opacity-100" style={frame} />
      <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[20px] overflow-hidden" style={bar}>
        {/* A slow sheen across the bar on hover: light moving over metal. */}
        <span className="absolute inset-0 -translate-x-full bg-[linear-gradient(90deg,transparent,rgba(255,255,255,.75),transparent)] opacity-0 transition-[transform,opacity] duration-700 ease-out group-hover:translate-x-full group-hover:opacity-100" />
      </span>
    </>
  );
}
